import { useState } from "react";
import { ui } from "@patient-watch/ui";
import { formComponents as fc } from "@/features/i18n/formComponents";
import { Badge } from "@patient-watch/ui/badge";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Building2, Plus, Star } from "lucide-react";
import { formatRoleLabel, useProfileCopy } from "@/features/i18n/profileCopy";
import FavouriteUserStar from "./FavouriteUserStar";
import type { TeamMember, UserOrganisation } from "../api/selectTeamMembers";
import { useAddInvitedUserToOrg } from "../api/useAddInvitedUserToOrg";
import { roleOptions } from "@/definitions/roles";
import { NameDialog, AdminEmailDialog } from "./EditProfileDialogs";
import RequiredInfo from "@/components/common/RequiredInfo";
import { useAuth } from "@/utils/auth";
import { useSecretaryOwnerCount, useSelectSecretaryOrganisations } from "../api/selectSecretaryOrganisations";
import {
    useAddSecretaryToOrganisation,
    useRemoveSecretaryFromOrganisation,
    useSetDefaultSecretaryForOrganisation,
} from "../api/useSecretaryOrganisationMutations";

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    member: TeamMember;
    currentUserOrganisations: UserOrganisation[];
}

const addOrgSchema = z.object({
    organisation_id: z.string().min(1, "Please select an organisation"),
    role: z.string().min(1, "Please select a role"),
});

type AddOrgFormType = z.infer<typeof addOrgSchema>;

const TeamMemberDialog = ({ open, onOpenChange, member, currentUserOrganisations }: Props) => {
    const copy = useProfileCopy();
    const auth = useAuth();
    const [addOrgDialogOpen, setAddOrgDialogOpen] = useState(false);
    const [removingOrg, setRemovingOrg] = useState<{ id: string; name: string } | null>(null);
    const addUserToOrg = useAddInvitedUserToOrg();

    const isSecretary = Boolean(member.roles?.includes("SECRETARY"));
    const { data: secretaryOrganisations = [] } = useSelectSecretaryOrganisations(isSecretary ? auth.id : "");
    const { data: secretaryOwnerCount = 0 } = useSecretaryOwnerCount(isSecretary ? member.user_id : "");
    // Not shared: only one clinician (this one) owns this secretary, so it's safe to correct their details.
    const canEditSecretaryDetails = isSecretary && member.canEditName && secretaryOwnerCount <= 1;
    const addSecretaryToOrg = useAddSecretaryToOrganisation();
    const setDefaultSecretary = useSetDefaultSecretaryForOrganisation();
    const removeSecretaryFromOrg = useRemoveSecretaryFromOrganisation();

    const name = `${member.first_name || ""} ${member.last_name || ""}`.trim() || "No name";
    const profileForEdit = {
        ...member,
        name: (member as { name?: string }).name ?? name,
    } as Parameters<typeof NameDialog>[0]["profile"];

    const memberOrgIds = new Set(
        ((member.organisations as Array<{ organisation_id: string }>) || []).map((o) => o.organisation_id)
    );

    // Organisations the member is NOT already in
    const availableOrgs = currentUserOrganisations.filter((org) => !memberOrgIds.has(org.organisation_id));

    const form = useForm<AddOrgFormType>({
        resolver: zodResolver(addOrgSchema),
        defaultValues: {
            organisation_id: "",
            role: "PATIENT",
        },
    });

    const onAddOrg = (data: AddOrgFormType) => {
        addUserToOrg.mutate(
            {
                userId: member.user_id,
                organisationId: data.organisation_id,
                role: data.role,
            },
            {
                onSuccess: () => {
                    setAddOrgDialogOpen(false);
                    form.reset();
                },
            }
        );
    };

    const confirmRemoveSecretary = () => {
        if (!removingOrg) return;
        removeSecretaryFromOrg.mutate(
            {
                secretaryId: member.user_id,
                ownerClinicianId: auth.id,
                organisationId: removingOrg.id,
            },
            { onSettled: () => setRemovingOrg(null) }
        );
    };

    return (
        <>
            <ui.Dialog open={open} onOpenChange={onOpenChange}>
                <ui.DialogContent className="max-w-md">
                    <ui.DialogHeader>
                        <ui.DialogTitle className="flex items-center gap-3">
                            <div className="flex justify-center items-center bg-slate-200 rounded-full w-10 h-10 font-medium text-slate-600 text-lg">
                                {(member.first_name?.[0] || member.email?.[0] || "?").toUpperCase()}
                            </div>
                            <div className="flex-1">
                                <div className="flex items-center gap-2">
                                    <span>{name}</span>
                                    <FavouriteUserStar userId={member.user_id} favourite={member.isFavourite} />
                                </div>
                                <p className="font-normal text-muted-foreground text-sm">{member.email}</p>
                            </div>
                        </ui.DialogTitle>
                    </ui.DialogHeader>

                    <ui.Card className="shadow-none border-0">
                        <ui.CardHeader>
                            <ui.CardTitle className="text-lg">Team member details</ui.CardTitle>
                        </ui.CardHeader>
                        <ui.CardContent className="space-y-6 pt-0">
                            {/* Name – same EditDialog pattern as PatientCard. For secretaries, only
                                editable here when not shared with other clinicians (canEditSecretaryDetails). */}
                            {(isSecretary ? canEditSecretaryDetails : member.canEditName) ? (
                                <NameDialog profile={profileForEdit} editable={true} locked={false} show={true} />
                            ) : (
                                <RequiredInfo
                                    label="Name"
                                    info={member.title ? `${member.title} ${name}` : name}
                                    noInfo={!name || name === "No name"}
                                />
                            )}

                            {/* Email – only when this secretary isn't shared with other clinicians. Uses
                                AdminEmailDialog (not the plain self-service EmailDialog): it correctly
                                targets this profile's own email as the "old" email via allowAuthEmailOverride,
                                which disables the self-service session-email fallback that caused the
                                original incident (see clinician-secretary-management-concerns.md). Backend
                                authorization for "clinician edits their exclusively-owned secretary's email"
                                lives in public-update-user-email. */}
                            {isSecretary && canEditSecretaryDetails ? (
                                <AdminEmailDialog profile={profileForEdit} editable={true} locked={false} show={true} />
                            ) : null}

                            {/* Organisations Section - for secretaries, each row also carries the
                                default/remove/add-as-secretary actions (merged from the old separate
                                "Secretary Organisations" section, which duplicated the same org list). */}
                            <div>
                                <div className="flex justify-between items-center mb-2">
                                    <h4 className="flex items-center gap-2 font-medium text-slate-700 text-sm">
                                        <Building2 size={14} />
                                        Organisations
                                    </h4>
                                    {member.canEditName && availableOrgs.length > 0 && (
                                        <ui.Button variant="ghost" size="sm" onClick={() => setAddOrgDialogOpen(true)}>
                                            <Plus size={14} className="mr-1" />
                                            Add
                                        </ui.Button>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    {(
                                        (member.organisations as Array<{
                                            organisation_id: string;
                                            organisation_name: string;
                                            role: string;
                                        }>) || []
                                    ).map((org) => {
                                        if (isSecretary && org.role === "SECRETARY") {
                                            const assignment = secretaryOrganisations.find(
                                                (a) =>
                                                    a.secretaryId === member.user_id &&
                                                    a.organisationId === org.organisation_id
                                            );
                                            return (
                                                <div
                                                    key={org.organisation_id}
                                                    className={
                                                        assignment?.isDefault
                                                            ? "flex justify-between items-center bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-800 p-2 rounded-md"
                                                            : "flex justify-between items-center bg-slate-50 p-2 rounded-md"
                                                    }
                                                >
                                                    <span className="text-sm">{org.organisation_name}</span>
                                                    {assignment ? (
                                                        <div className="flex items-center gap-2">
                                                            {assignment.isDefault ? (
                                                                <Badge className="gap-1 bg-indigo-100 hover:bg-indigo-100 dark:bg-indigo-950 dark:hover:bg-indigo-900 dark:text-indigo-200 text-indigo-700 text-xs">
                                                                    <Star
                                                                        size={12}
                                                                        className="fill-indigo-500 text-indigo-500"
                                                                    />
                                                                    {copy.secretaryDefaultBadge}
                                                                </Badge>
                                                            ) : (
                                                                <>
                                                                    <ui.Button
                                                                        type="button"
                                                                        variant="secondary"
                                                                        size="sm"
                                                                        disabled={setDefaultSecretary.isPending}
                                                                        onClick={() =>
                                                                            setDefaultSecretary.mutate({
                                                                                secretaryId: member.user_id,
                                                                                ownerClinicianId: auth.id,
                                                                                organisationId: org.organisation_id,
                                                                            })
                                                                        }
                                                                    >
                                                                        {copy.setAsDefaultSecretary}
                                                                    </ui.Button>
                                                                    <ui.Button
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        className="hover:bg-red-50 text-red-600 hover:text-red-700"
                                                                        disabled={removeSecretaryFromOrg.isPending}
                                                                        onClick={() =>
                                                                            setRemovingOrg({
                                                                                id: org.organisation_id,
                                                                                name: org.organisation_name,
                                                                            })
                                                                        }
                                                                    >
                                                                        {copy.removeAsMySecretary}
                                                                    </ui.Button>
                                                                </>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <ui.Button
                                                            variant="ghost"
                                                            size="sm"
                                                            disabled={addSecretaryToOrg.isPending}
                                                            onClick={() =>
                                                                addSecretaryToOrg.mutate({
                                                                    secretaryId: member.user_id,
                                                                    ownerClinicianId: auth.id,
                                                                    organisationId: org.organisation_id,
                                                                })
                                                            }
                                                        >
                                                            <Plus size={14} className="mr-1" />
                                                            {copy.addAsMySecretary}
                                                        </ui.Button>
                                                    )}
                                                </div>
                                            );
                                        }

                                        return (
                                            <div
                                                key={org.organisation_id}
                                                className="flex justify-between items-center bg-slate-50 p-2 rounded-md"
                                            >
                                                <span className="text-sm">{org.organisation_name}</span>
                                                <Badge variant="secondary" className="text-xs">
                                                    {formatRoleLabel(org.role, copy)}
                                                </Badge>
                                            </div>
                                        );
                                    })}
                                    {(!member.organisations ||
                                        (member.organisations as Array<unknown>).length === 0) && (
                                        <p className="text-muted-foreground text-sm">No organisations</p>
                                    )}
                                </div>
                            </div>
                        </ui.CardContent>
                    </ui.Card>
                </ui.DialogContent>
            </ui.Dialog>

            {/* Add to organisation – popup dialog (same pattern as NameDialog) */}
            <ui.Dialog open={addOrgDialogOpen} onOpenChange={setAddOrgDialogOpen}>
                <ui.DialogContent className="min-w-[320px] max-w-md">
                    <ui.DialogHeader>
                        <ui.DialogTitle>Add to organisation</ui.DialogTitle>
                        <ui.DialogDescription>
                            Add this team member to an organisation and choose their role.
                        </ui.DialogDescription>
                    </ui.DialogHeader>
                    <form onSubmit={form.handleSubmit(onAddOrg)} className="space-y-4 pt-2">
                        <div>
                            <ui.Label htmlFor="add-org-organisation_id">Organisation</ui.Label>
                            <ui.Select
                                value={form.watch("organisation_id")}
                                onValueChange={(value) => form.setValue("organisation_id", value)}
                            >
                                <ui.SelectTrigger id="add-org-organisation_id" className="mt-1">
                                    <ui.SelectValue placeholder="Select organisation" />
                                </ui.SelectTrigger>
                                <ui.SelectContent>
                                    {availableOrgs.map((org) => (
                                        <ui.SelectItem key={org.organisation_id} value={org.organisation_id}>
                                            {org.organisation_name}
                                        </ui.SelectItem>
                                    ))}
                                </ui.SelectContent>
                            </ui.Select>
                            {form.formState.errors.organisation_id && (
                                <p className="mt-1 text-red-500 text-sm">
                                    {form.formState.errors.organisation_id.message}
                                </p>
                            )}
                        </div>
                        <div>
                            <ui.Label htmlFor="add-org-role">Role</ui.Label>
                            <ui.Select
                                value={form.watch("role")}
                                onValueChange={(value) => form.setValue("role", value)}
                            >
                                <ui.SelectTrigger id="add-org-role" className="mt-1">
                                    <ui.SelectValue placeholder="Select role" />
                                </ui.SelectTrigger>
                                <ui.SelectContent>
                                    {roleOptions.map((role) => (
                                        <ui.SelectItem key={role.value} value={role.value}>
                                            {formatRoleLabel(role.value, copy)}
                                        </ui.SelectItem>
                                    ))}
                                </ui.SelectContent>
                            </ui.Select>
                        </div>
                        <ui.DialogFooter>
                            <fc.CancelButton
                                variant="outline"
                                size="default"
                                onClick={() => {
                                    setAddOrgDialogOpen(false);
                                    form.reset();
                                }}
                                className="w-auto"
                            />
                            <fc.AddButton
                                buttonType="submit"
                                variant="default"
                                label="Add to organisation"
                                isLoading={addUserToOrg.isPending}
                                className="w-auto"
                            />
                        </ui.DialogFooter>
                    </form>
                </ui.DialogContent>
            </ui.Dialog>

            {/* Remove-as-secretary confirmation */}
            <ui.AlertDialog open={!!removingOrg} onOpenChange={(open) => !open && setRemovingOrg(null)}>
                <ui.AlertDialogContent className="max-w-md">
                    <ui.AlertDialogHeader>
                        <ui.AlertDialogTitle>Are you sure?</ui.AlertDialogTitle>
                        <ui.AlertDialogDescription>
                            This will remove <span className="font-medium">{name}</span> as your secretary for{" "}
                            <span className="font-medium">{removingOrg?.name}</span>.
                        </ui.AlertDialogDescription>
                    </ui.AlertDialogHeader>
                    <ui.AlertDialogFooter className="flex md:flex-row flex-col gap-y-4">
                        <ui.AlertDialogAction asChild>
                            <fc.DeleteButton
                                variant="destructive"
                                size="lg"
                                isLoading={removeSecretaryFromOrg.isPending}
                                onClick={confirmRemoveSecretary}
                                className="w-auto"
                            />
                        </ui.AlertDialogAction>
                        <ui.AlertDialogCancel asChild>
                            <fc.CancelButton variant="outline" size="lg" className="w-auto" />
                        </ui.AlertDialogCancel>
                    </ui.AlertDialogFooter>
                </ui.AlertDialogContent>
            </ui.AlertDialog>
        </>
    );
};

export default TeamMemberDialog;
